import LinearProgress from '@mui/material/LinearProgress';
import { appRoutes } from 'app/app-routes';
import { formValidationSchema } from 'common/functions/form-validation-schema';
import { Formik } from 'formik';
import { User, UserUpdate_Input, useUsersUpdateMutation } from 'graphql/generated';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { NavLink, useNavigate } from 'react-router-dom';
import { cacheActions, cacheSelectors } from 'redux/cache.slice';
import { RootState } from 'redux/root-reducer';
import { WsFormCheckbox } from 'stories/forms/WsFormCheckbox';
import { WsFormTextField } from 'stories/forms/WsFormTextField';
import { WsButton } from 'stories/inputs/WsButton';
import WsFlexbox from 'stories/layout/WsFlexbox';
import WsFormBox from 'stories/layout/WsFormBox';
import WsFormInfoContainer from 'stories/layout/WsFormInfoContainer';
import WsPanel from 'stories/layout/WsPanel';
import * as yup from 'yup';

interface FormValues extends Pick<User, 'email' | 'password' | 'firstName' | 'lastName' | 'zipCode'> {
  passwordConfirmation: string;
  changePw: boolean;
}

const validationSchema = yup.object().shape({
  changePw: yup.boolean(),
  email: formValidationSchema.email,
  password: yup.string().when('changePw', {
    is: true,
    then: formValidationSchema.password,
  }),
  passwordConfirmation: yup.string().when('changePw', {
    is: true,
    then: formValidationSchema.passwordConfirm,
  }),
  zipCode: formValidationSchema.zipCode,
  firstName: formValidationSchema.firstName,
  lastName: formValidationSchema.lastName,
});

const ChangeUserInfo = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [updateUser] = useUsersUpdateMutation();

  const user = useSelector((state: RootState) => cacheSelectors.user(state));

  const initialValues: FormValues = {
    changePw: false,
    email: user?.email || '',
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    zipCode: user?.zipCode || '',
    password: '',
    passwordConfirmation: '',
  };

  const formAction = async (formValues: FormValues, setSubmitting: (isSubmitting: boolean) => void) => {
    try {
      if (!user?.id) throw new Error('invalid user id');
      setSubmitting(true);
      const user_input: UserUpdate_Input = {
        id: user.id,
        firstName: formValues.firstName,
        lastName: formValues.lastName,
        email: formValues.email,
        zipCode: formValues.zipCode,
        password: formValues.changePw ? formValues.password : undefined,
        verified: user?.verified || false,
      };

      const data = await updateUser({
        variables: { object: user_input },
      });
      setSubmitting(false);

      if (data.data?.usersUpdate) {
        const { __typename, ...newUser } = data.data?.usersUpdate;
        dispatch(cacheActions.user(newUser));
        navigate(appRoutes.profile);
        dispatch(
          cacheActions.addSnack({
            message: 'User information updated',
            variant: 'success',
          }),
        );
      } else throw new Error('Update user error');
    } catch (error: any) {
      dispatch(
        cacheActions.addSnack({
          message: error.message,
          variant: 'error',
        }),
      );
    }
  };

  return (
    <Formik
      validationSchema={validationSchema}
      initialValues={initialValues}
      onSubmit={(values, { setSubmitting }) => formAction(values, setSubmitting)}
    >
      {props => (
        <WsPanel width="400px" title="Create an Account">
          <form onSubmit={props.handleSubmit}>
            <WsFormInfoContainer>Fields marked with (*) are required.</WsFormInfoContainer>
            <WsFormInfoContainer>
              Passwords must contain at least 8 characters, including at least one uppercase letter, one lowercase
              letter, and one number or special symbol (1,2,3,#,&,%,etc.).
            </WsFormInfoContainer>
            <WsFormBox>
              <WsFormTextField id="email" label="Email" type="email" fullWidth formik={props} disabled />
              <WsFormTextField id="firstName" label="First Name" type="text" fullWidth formik={props} />
              <WsFormTextField id="lastName" label="Last Name" type="text" fullWidth formik={props} />
              <WsFormTextField id="zipCode" label="Zip Code" type="text" fullWidth formik={props} />
              <WsFlexbox centered>
                <WsFormCheckbox id="changePw" label="Change Password" formik={props} />
              </WsFlexbox>
              <WsFormTextField
                id="password"
                label="Password"
                type="password"
                fullWidth
                formik={props}
                disabled={!props.values.changePw}
              />
              <WsFormTextField
                id="passwordConfirmation"
                label="Password Confirmation"
                type="password"
                fullWidth
                formik={props}
                disabled={!props.values.changePw}
              />
              {props.isSubmitting && <LinearProgress />}
              <WsButton label="Save Changes" type="submit" disabled={props.isSubmitting} />
              <WsFlexbox centered sx={{ marginTop: 1 }}>
                <NavLink to={appRoutes.profile}>
                  <WsButton label="Cancel" color="primary" variant="outlined" />
                </NavLink>
              </WsFlexbox>
            </WsFormBox>
          </form>
        </WsPanel>
      )}
    </Formik>
  );
};

export default ChangeUserInfo;
